'use client';

import { useI18n } from '@/contexts/I18nContext';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Film } from 'lucide-react';

export interface MentionItem {
  id: string;
  title: string;
  kind: 'document' | 'video';
}

interface CannyMentionMenuProps {
  query: string;
  items: MentionItem[];
  onPick: (item: MentionItem) => void;
  onClose: () => void;
}

/**
 * Меню по `@`: документы бренда и ролики библиотеки. Клавиатура обрабатывается
 * здесь же через window-слушатель — по образцу MentionPopup студии, чтобы
 * композеру не приходилось прокидывать события внутрь.
 */
export function CannyMentionMenu({ query, items, onPick, onClose }: CannyMentionMenuProps) {
  const { t } = useI18n();
  const [activeIndex, setActiveIndex] = useState(0);
  const activeRef = useRef<HTMLButtonElement>(null);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return items.slice(0, 8);
    return items.filter((item) => item.title.toLowerCase().includes(normalized)).slice(0, 8);
  }, [items, query]);

  // Держим активную строку в пределах списка: запрос мог сузить его,
  // пока активной была четвёртая позиция.
  useEffect(() => {
    setActiveIndex((prev) => (filtered.length === 0 ? 0 : Math.min(prev, filtered.length - 1)));
  }, [filtered.length]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (filtered.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % filtered.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        onPick(filtered[activeIndex]);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeIndex, filtered, onClose, onPick]);

  if (filtered.length === 0) {
    return (
      <div className="liquid-glass absolute bottom-full mb-2 w-full max-w-md rounded-2xl px-4 py-3 text-xs text-white/45">
        {t('canny.mention.empty')}
      </div>
    );
  }

  return (
    <div className="liquid-glass absolute bottom-full mb-2 max-h-64 w-full max-w-md overflow-y-auto rounded-2xl p-1">
      {filtered.map((item, index) => (
        <button
          key={`${item.kind}:${item.id}`}
          ref={index === activeIndex ? activeRef : undefined}
          type="button"
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => onPick(item)}
          className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-start text-sm transition-colors ${
            index === activeIndex ? 'bg-white/10 text-white' : 'text-white/70'
          }`}
        >
          {item.kind === 'document' ? (
            <FileText className="h-3.5 w-3.5 shrink-0 text-white/40" />
          ) : (
            <Film className="h-3.5 w-3.5 shrink-0 text-white/40" />
          )}
          <span className="truncate">{item.title}</span>
          <span className="ms-auto shrink-0 text-[11px] text-white/30">
            {t(item.kind === 'document' ? 'canny.mention.document' : 'canny.mention.video')}
          </span>
        </button>
      ))}
    </div>
  );
}
